import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IRowData } from "@/src/types/applicationOnline";
import { getTasksTableRowId, ITasksTablePayload, ITasksTableRow } from "@/src/types/tasksTable";
import {
  normalizeVisibleTOPColumns,
  tasksAllFieldsList,
  tasksDefaultConfiguration,
  tasksLegacyColumnIdMap,
  TasksColumnConfig,
} from "@/src/constants/tasksTableColumns";
import { getUserConfigFromStorage } from "@/src/utils/userConfigStorage";

interface TasksTableState {
  visibleColumns: TasksColumnConfig[];
  hiddenColumns: TasksColumnConfig[];
  data: ITasksTableRow[];
  selectedRows: number[];
}

const userConfig = getUserConfigFromStorage();
const initialVisibleColumns = normalizeVisibleTOPColumns(userConfig?.TOPConfig?.TOPTable ?? tasksDefaultConfiguration);

const initialState: TasksTableState = {
  visibleColumns: initialVisibleColumns,
  hiddenColumns: tasksAllFieldsList.filter((col) => !initialVisibleColumns.some((visibleCol) => visibleCol.id === col.id)),
  data: [],
  selectedRows: [],
};

const tasksTableSlice = createSlice({
  name: "tasksTable",
  initialState,
  reducers: {
    setData(state, action: PayloadAction<ITasksTablePayload>) {
      state.data = action.payload.data;
    },
    toggleColumnVisibility(state, action: PayloadAction<string>) {
      const colId = tasksLegacyColumnIdMap[action.payload] ?? action.payload;
      const isVisible = state.visibleColumns.some((col) => col.id === colId);
      if (isVisible) {
        const col = state.visibleColumns.find((col) => col.id === colId)!;
        state.visibleColumns = state.visibleColumns.filter((col) => col.id !== colId);
        state.hiddenColumns.push({ title: col.title, id: col.id });
      } else {
        let col = state.hiddenColumns.find((col) => col.id === colId);
        if (!col) {
          col = tasksAllFieldsList.find((c) => c.id === colId)!;
        }
        state.hiddenColumns = state.hiddenColumns.filter((c) => c.id !== colId);
        state.visibleColumns.push({ title: col.title, id: col.id, width: col.width ?? 4 });
      }
    },
    setColumnWidth(state, action: PayloadAction<{ id: string; width: number }>) {
      const colId = tasksLegacyColumnIdMap[action.payload.id] ?? action.payload.id;
      const { width } = action.payload;
      const col = state.visibleColumns.find((c) => c.id === colId);
      if (col) col.width = width;
    },
    updateRow(state, action: PayloadAction<IRowData>) {
      const updatedRow = action.payload;
      const index = state.data.findIndex((row) => row.task_id === updatedRow.applyInfo.id);
      if (index !== -1) {
        state.data[index] = {
          ...state.data[index],
          full_name: updatedRow.userInfo.fullname,
          date_of_birth: updatedRow.userInfo.dateOfBirth,
        };
      }
    },
    toggleRowSelection(state, action: PayloadAction<number>) {
      const id = action.payload;
      if (state.selectedRows.includes(id)) {
        state.selectedRows = state.selectedRows.filter((rowId) => rowId !== id);
      } else {
        state.selectedRows.push(id);
      }
    },
    toggleAllRows(state) {
      if (state.selectedRows.length === state.data.length) {
        state.selectedRows = [];
      } else {
        state.selectedRows = state.data.map((row) => getTasksTableRowId(row));
      }
    },
    setActualFieldValue(state, action: PayloadAction<{ id: number; fieldActualValue: string; keyField: string }>) {
      const { id, fieldActualValue, keyField } = action.payload;
      const row = state.data.find((r) => r.task_id === id);
      if (!row) return;

      switch (keyField) {
        case "color_star":
          row.color_star = fieldActualValue;
          break;
        case "status":
          row.status = fieldActualValue;
          break;
        default:
          break;
      }
    },
    patchTaskRow(state, action: PayloadAction<{ taskId: number; patch: Partial<ITasksTableRow> }>) {
      const row = state.data.find((r) => r.task_id === action.payload.taskId);
      if (!row) return;
      Object.assign(row, action.payload.patch);
    },
    showAllColumns(state) {
      state.visibleColumns = [...tasksAllFieldsList];
      state.hiddenColumns = [];
    },
    hideAllColumns(state) {
      state.hiddenColumns = [...tasksAllFieldsList];
      state.visibleColumns = [];
    },
    setVisibleColumns(state, action: PayloadAction<TasksColumnConfig[]>) {
      const normalized = normalizeVisibleTOPColumns(action.payload);
      state.visibleColumns = normalized;
      state.hiddenColumns = tasksAllFieldsList.filter((col) => !normalized.some((visibleCol) => visibleCol.id === col.id));
    },
  },
});

export const {
  setData,
  toggleColumnVisibility,
  setColumnWidth,
  toggleRowSelection,
  toggleAllRows,
  setActualFieldValue,
  patchTaskRow,
  updateRow,
  showAllColumns,
  hideAllColumns,
  setVisibleColumns,
} = tasksTableSlice.actions;

export default tasksTableSlice.reducer;
